import {
  ButtonGhostV2,
  colors,
  SANS_2,
  SANS_3,
  icons,
  TooltipV2,
  colors2,
  CheckboxButton,
  UI_CAPTION_SEMIBOLD_DF,
} from "oolib";
import React, { useState } from "react";
import { useEffect } from "react";
import { useTheme } from "styled-components";
import { _Locale } from "../../../../locale/Locale";
import {
  StyledCheckboxContainer,
  StyledDataTableHeader,
  StyledDataTableHeaderCell,
  StyledDataTableHeaderCellText,
  StyledDataTableHeaderCellTextCompact,
  StyledHeaderCellContentWrapper,
  StyledHeaderErrorBanner,
  StyledInput,
  StyledResizeDetector,
  StyledResizeFeedback,
} from "../../styled";
import { ActionMenu } from "../../../../UI/ActionMenu";
import { checkIfSortPathsAreMatching } from "../../utils/tableSortUtils";
import { ColumnSortDropdown } from "./comps/ColumnSortDropdown";
import { ChangeFieldCompDropdown } from "./comps/ChangeFieldCompDropdown";
import { useResizeDataTableColumns } from "./utils/useResizeDataTableColumns";
import { ROW_HEIGHT } from "../..";
import { DisplayIcon } from "oolib/dist/utils/comps/DisplayIcon";
const { ArrowDown, ArrowUp } = icons;

const getErrorMessage = (errorType) => {
  switch (errorType) {
    case "required":
      return _Locale("Field name required");
    case "invalidName":
      return _Locale(
        "This name conflicts with system-reserved terms ('Tag', 'Themes', 'Subthemes')"
      );
    case "duplicate":
      return _Locale("Field name already exists");
    case "mappingRequired":
      return _Locale("Select an existing field to map this column to");
    case "duplicateMapping":
      return _Locale("Two columns can't map to the same existing field");
    default:
      return null;
  }
};

const TableHeaderElem = ({
  tableConfig,
  headerEditable,
  handleTableSort,
  activeSorts,
  actions,
  bulkActions,
  colWidthConfig,
  setColWidthConfig,
  backendActiveSort,
  builderProps,
  tableRowIds,
  loadedRowsCount,
  bulkActions_selectedRowIds,
  bulkActions_setSelectedRowIds,
  headerValidationErrors = {},
  onHeaderNameUpdate,
  validateSingleHeader,
  enableContrastHeaderStyle = false,
  withStickyHeader,
  stickyHeaderTopOffset,
  minColWidth = 200,
  /**
   * ⚠️ CUSTOM — for ONE specific use case ONLY: the self-managed listing page's
   * per-column "change field type" header control. NOT a general-purpose
   * TableHeaderElem prop, and NOT the way to add header controls in general —
   * if you need something similar, design a proper API instead of widening
   * this.
   *
   * Shape: { contentType }. Threaded down from TableElem; set only by
   * ListingPage's TableStyle when SELF_MANAGED_BASE_CONFIGS is present, so it's
   * undefined on every other table — which is exactly what gates the control.
   */
  custom_listingPageChangeFieldCompProps,
}) => {
  const isBackendSort = !!backendActiveSort;

  const sortConfigExists = (d) =>
    isBackendSort ? !!d.backendSortConfig : !!d.sortConfig;

  const genArrows = (d) => {
    if (!sortConfigExists(d)) return null;
    //else

    if (isBackendSort) {
      return (
        <div className="TableElem__HeaderCellSortBtn">
          {checkIfSortPathsAreMatching(
            backendActiveSort.sort,
            d.backendSortConfig.sort
          ) ? (
            Object.values(backendActiveSort.sort)[0] === 1 ? (
              <ArrowUp size={16} /> //ascending
            ) : (
              <ArrowDown size={16} />
            ) //descending
          ) : null}
        </div>
      );
    } else {
      return (
        <div className="TableElem__HeaderCellSortBtn">
          {activeSorts[d.colId] === "a" ? (
            <ArrowUp size={16} /> //ascending
          ) : activeSorts[d.colId] === "b" ? (
            <ArrowDown size={16} /> //descending
          ) : null}
        </div>
      );
    }
  };

  const theme = useTheme();
  // Dense tables (tableConfig.compactHeader) get the caption-sized header text.
  const HeaderText = tableConfig.compactHeader ? StyledDataTableHeaderCellTextCompact : StyledDataTableHeaderCellText;

  const {
    resetColResizeState,
    initColResizeState,
    handleResizeColumn,
    colResizeState,
    hoveredDetector,
    handleDetectorMouseEnter,
    handleDetectorMouseLeave,
  } = useResizeDataTableColumns({
    setColWidthConfig,
    colWidthConfig,
    theme,
    builderProps,
    minColWidth,
  });

  useEffect(() => {
    window.addEventListener("mouseup", resetColResizeState);
    window.addEventListener("mousemove", handleResizeColumn);
    return () => {
      window.removeEventListener("mousemove", handleResizeColumn);
      window.removeEventListener("mouseup", resetColResizeState);
    };
  }, [resetColResizeState, handleResizeColumn]);

  const [hoveredCellId, setHoveredCellId] = useState(null);

  const renderHeaderCellContent = (d, i) => (
    <>
      <StyledHeaderCellContentWrapper
        style={{ marginLeft: "1rem" }}
        title={d.headerDisplay}
        headerEditable={headerEditable}
        onMouseEnter={() => !headerEditable && setHoveredCellId(d.colId)}
        onMouseLeave={() => !headerEditable && setHoveredCellId(null)}
      >
        {headerEditable ? (
          <>
            <StyledDataTableHeaderCellText
              style={{ flexGrow: 1, height: "100%" }}
            >
              <StyledInput
                defaultValue={_Locale(d.headerDisplay)}
                onBlur={(e) => {
                  const newValue = e.target.value;
                  if (onHeaderNameUpdate && d.colId) {
                    onHeaderNameUpdate(d.colId, newValue);
                  }
                  if (validateSingleHeader && d.colId) {
                    validateSingleHeader(d.colId, newValue);
                  }
                }}
              />
            </StyledDataTableHeaderCellText>
          </>
        ) : (
          <>
            {d.headerDisplay && (
              <HeaderText semibold>
                {_Locale(d.headerDisplay)}
              </HeaderText>
            )}
            {d.infoTooltip ? (
              // `title=""` suppresses the parent's native tooltip ONLY when
              // hovering the info-icon — so the column-header text still
              // shows its native title, but the icon shows just its
              // TooltipV2 with no overlap.
              <span title="" style={{ display: "inline-flex", alignItems: "center" }}>
                <TooltipV2 {...d.infoTooltip}>
                  <icons.Info size={16} color={colors2.grey60} />
                </TooltipV2>
              </span>
            ) : null}
          </>
        )}
        {/* Right-aligned controls group. Kept together (not spread by the
            wrapper's justify-content:space-between) so the change-type pencil
            sits right next to the sort icon at the column's right edge. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "auto",
          }}
        >
          {sortConfigExists(d) && (
            <ColumnSortDropdown
              colData={d}
              isBackendSort={isBackendSort}
              backendActiveSort={backendActiveSort}
              activeSorts={activeSorts}
              isHovered={hoveredCellId === d.colId}
              onSortClick={(asc_desc) => {
                let value = isBackendSort
                  ? asc_desc === "ascending"
                    ? 1
                    : -1
                  : asc_desc === "ascending"
                  ? "a"
                  : "b";
                handleTableSort(d, value);
              }}
            />
          )}
          {!headerEditable && custom_listingPageChangeFieldCompProps?.contentType && (
            <ChangeFieldCompDropdown
              colData={d}
              contentType={custom_listingPageChangeFieldCompProps.contentType}
              fieldTypeChangeDisabled={
                custom_listingPageChangeFieldCompProps.fieldTypeChangeDisabled
              }
              isHovered={hoveredCellId === d.colId}
            />
          )}
        </div>
      </StyledHeaderCellContentWrapper>
      {d.subColsConfig && (
        <div style={{ display: "flex" }}>
          {d.subColsConfig.map((s, i) => (
            <StyledHeaderCellContentWrapper
              style={{
                width: `${100 / d.subColsConfig.length}%`,
                borderTop: `1px solid ${colors2.grey10}`,
                borderRight:
                  i !== d.subColsConfig.length - 1 &&
                  `1px solid ${colors2.grey10}`,
              }}
              title={s.headerDisplay}
            >
              <StyledDataTableHeaderCellText bold>
                {s.headerDisplay}
              </StyledDataTableHeaderCellText>
              {s.infoTooltip ? (
                <TooltipV2 {...s.infoTooltip}>
                  <icons.Info size={16} color={colors2.grey60} />
                </TooltipV2>
              ) : null}
            </StyledHeaderCellContentWrapper>
          ))}
        </div>
      )}
      {headerValidationErrors[d.colId] && (
        <StyledHeaderErrorBanner>
          <div style={{ paddingBlock: "0.2rem" }}>
            <DisplayIcon
              size={12}
              icon="WarningDiamond"
              weight="fill"
              color={colors2.white}
            />
          </div>
          <UI_CAPTION_SEMIBOLD_DF>
            {getErrorMessage(headerValidationErrors[d.colId])}
          </UI_CAPTION_SEMIBOLD_DF>
        </StyledHeaderErrorBanner>
      )}
      <StyledResizeDetector
        onMouseDown={(e) => initColResizeState(e, d)}
        onMouseEnter={() => handleDetectorMouseEnter(d.colId)}
        onMouseLeave={handleDetectorMouseLeave}
      >
        <StyledResizeFeedback
          isHovered={hoveredDetector === d.colId}
          isResizing={colResizeState?.colId === d.colId}
          theme={theme}
        />
      </StyledResizeDetector>
    </>
  );

  // hoisted out of the per-cell loop — same for every header cell
  const headerCellHeight = tableConfig.cellData.some((d) => d.subColsConfig)
    ? ROW_HEIGHT * 2 + "px"
    : ROW_HEIGHT + "px";

  const selectedRowsCount = bulkActions_selectedRowIds.length;
  // Prefer loadedRowsCount (primitive from data.length) — tableRowIds is a
  // useMemo with side effects upstream and lags one render behind growth.
  // Rows a host has taken out of bulk selection (tableConfig.disabledRowIds)
  // don't count toward select-all or its mixture state. The base stays
  // loadedRowsCount (see above); only the disabled rows among the loaded ids
  // are subtracted, so the one-render lag of tableRowIds can't skew the total.
  const disabledRowIds = new Set(tableConfig?.disabledRowIds || []);
  const selectableRowIds = disabledRowIds.size
    ? (tableRowIds || []).filter((id) => !disabledRowIds.has(id))
    : tableRowIds;
  const disabledLoadedCount = disabledRowIds.size
    ? (tableRowIds || []).filter((id) => disabledRowIds.has(id)).length
    : 0;
  const totalNumberOfRows = (loadedRowsCount ?? tableRowIds?.length ?? 0) - disabledLoadedCount;

  return (
    <StyledDataTableHeader
      style={{
        background: enableContrastHeaderStyle
          ? `linear-gradient(${colors2.grey5}, ${colors2.grey5}), linear-gradient(${colors2.white}, ${colors2.white})`
          : colors2.white,
        ...(withStickyHeader && {
          position: "sticky",
          // sits directly below customTopComp, which is itself sticky at top 0
          top: stickyHeaderTopOffset ? `${stickyHeaderTopOffset}px` : "6.4rem",
          zIndex: 1,
        }),
      }}
      // onMouseMove={handleResizeColumn}
    >
      {bulkActions && (
        <StyledCheckboxContainer
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <CheckboxButton
            /*
              Displays the minus icon when at least one row is selected but not all rows are selected:
              selectedRowsCount > 0 && selectedRowsCount !== totalNumberOfRows
            */
            isSelected={selectedRowsCount > 0}
            isMixture={selectedRowsCount !== totalNumberOfRows}
            // CheckboxButton's onClick still fires when disabled (only the
            // visual changes), so we also short-circuit onChange here as a
            // defensive guard.
            disabled={!totalNumberOfRows}
            onChange={(k, v) => {
              if (!totalNumberOfRows) return;
              if (v === true) {
                // Select All - Add all rowIds to bulkActions_selectedRowIds array
                bulkActions_setSelectedRowIds(selectableRowIds);
              } else {
                // Deselect All - Clicking on the check icon or minus icon deselects all rows
                bulkActions_setSelectedRowIds([]);
              }
            }}
          />
        </StyledCheckboxContainer>
      )}
      {tableConfig.cellData.map((d, i) => {
        const hasActiveSort = isBackendSort
          ? checkIfSortPathsAreMatching(
              backendActiveSort?.sort,
              d?.backendSortConfig?.sort
            )
          : activeSorts?.[d?.colId] && activeSorts[d.colId] !== "none";

        return (
          <StyledDataTableHeaderCell
            freezeFirstCol={tableConfig.freezeFirstCol}
            cellIdx={i}
            id="TableHeaderCell"
            data-col-id={d.colId}
            key={d.colId}
            hasError={!!headerValidationErrors[d.colId]}
            headerEditable={headerEditable}
            isLastCell={i === tableConfig.cellData.length - 1}
            // no plus button / ellipsis column after the cells: the last
            // cell sits on the table's edge
            isTableEdge={!builderProps && !actions && i === tableConfig.cellData.length - 1}
            style={{
              width: colWidthConfig[d.colId],
              height: headerCellHeight,
              backgroundColor: d.disabled && colors2.grey5,
              color: d.disabled && colors2.grey40,
              borderBottom: hasActiveSort
                ? `1px solid ${colors2.grey60}`
                : undefined,
            }}
          >
            {renderHeaderCellContent(d, i)}
          </StyledDataTableHeaderCell>
        );
      })}
      {builderProps && (
        <ButtonGhostV2
          iconSize="S"

          icon="Plus"
          onClick={() => builderProps.showColumnConfigureTools()}
        />
      )}
      {actions && (
        /**
         * Invisible twin of the row-level ActionMenu cell (TableRowElem renders
         * the real one). The header is what sets the table's width — virtualized
         * rows are position:absolute and contribute nothing — so without this
         * spacer the actions column overflows outside the table border.
         * visibility:hidden (NOT display:none) so it still occupies its width,
         * and since it's the same markup as the row cell, the reserved width
         * tracks any future ActionMenu dimension changes automatically.
         */
        <div style={{ padding: "0.6rem", visibility: "hidden" }} aria-hidden="true">
          <ActionMenu actions={[{ display: "" }]} />
        </div>
      )}
    </StyledDataTableHeader>
  );
};

export default TableHeaderElem;
